'use client'

import type { KeyboardEvent, Ref } from 'react'

import { useTranslation } from '@/hooks/client/use-translation'
import { formatOfferPrice as formatPrice } from '@/lib/prices/format-offer-price'
import { tw } from '@/lib/utils'

import type { PriceLevelTab } from './price-section-types'

interface PriceLadderTileProps {
  level: PriceLevelTab
  selected: boolean
  onSelect: () => void
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void
  buttonRef: Ref<HTMLButtonElement>
}

export const PriceLadderTile = ({ level, selected, onSelect, onKeyDown, buttonRef }: PriceLadderTileProps) => {
  const { t } = useTranslation()
  return (
    <button
      ref={buttonRef}
      type="button"
      role="radio"
      aria-checked={selected}
      tabIndex={selected ? 0 : -1}
      data-tracking="ignore"
      onClick={onSelect}
      onKeyDown={onKeyDown}
      className={tw(
        'relative flex min-w-0 cursor-pointer flex-col items-start gap-2 rounded-xl border px-3 py-3 text-left text-navy transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lapis',
        selected ? 'border-lapis bg-blue-50 ring-1 ring-lapis' : 'border-navy-20 hover:border-lapis'
      )}
    >
      <span className="pr-3 text-sm font-semibold">
        {level.name} · {level.levelName}
      </span>
      {selected && (
        <span aria-hidden="true" className="absolute top-3 right-2 text-xs font-bold text-lapis">
          ✓
        </span>
      )}
      <span className="text-xs text-navy-60">
        {level.lowestPrice != null ? (
          <>
            {t('price.from', 'From')}{' '}
            <strong className="font-medium text-navy">
              {formatPrice(level.lowestPrice)} / {t('price.pair', 'pair')}
            </strong>
          </>
        ) : (
          t('product.purchase.noPrice', 'No price available')
        )}
      </span>
    </button>
  )
}
